import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Drawer,
  Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ReactComponent as UploadIcon } from "../../../../images/uploadIcon.svg";
import { Description, Close } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailConversationHelp1 from "../../../../images/EmailConversationHelp1.svg";
import EmailConversationHelp2 from "../../../../images/EmailConversationHelp2.svg";
import EmailConversationHelp3 from "../../../../images/EmailConversationHelp3.svg";
import AddIcon from "@mui/icons-material/Add";
import AddSourcePopup from "./AddAnotherSource";
import { uploadDocumentToTransformer } from "../../../../services/webServices";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";
import MessagePopup from "../../../popup-components/MessagePopup";
import LinearProgressBar from "../../../common-components/QMentisLinearProgressBar";

const EmailCommunications = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const allowedFormats = [".eml", ".msg", ".mbox"];

    const invalidFiles = files.filter((file) => {
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      return !allowedFormats.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      setErrorMessage(
        `Unsupported file format(s). Please check the file format.`
      );
      setIsErrorPopupOpen(true);
      return;
    }

    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAnalyzeFiles = async () => {
    try {
      setIsAnalyzing(true);

      // Upload all files together in one request
      const uploadResponse = await uploadDocumentToTransformer(uploadedFiles);

      // Use the response data directly
      const resultData = uploadResponse.data.structuredCSV;
      console.log("resultData is---->", resultData);

      // Navigate with the result data
      navigate("/dashboard/qe-strategy", {
        state: { outputData: resultData },
      });
    } catch (error) {
      console.error("Unexpected error:", error);

      if (error.response?.data?.message === "Your session has expired.") {
        setSessionExpired(true);
        setIsErrorPopupOpen(false);
      } else {
        // Show popup for all other errors
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred. Please try again.";
        setErrorMessage(msg);
        setIsErrorPopupOpen(true);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: 1000, xl: 1200 },
          maxWidth: "1400px",
          mx: "auto",
          mt: 2,
        }}
      >
        {/* Upload Area (Box 1) */}
        <Box
          sx={{
            backgroundColor: "#fff",
            boxShadow: "0 0 0 1px #f0f0f0",
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
          }}
        >
          {/* Title with Info Button */}
          <Typography
            sx={{
              mt: -2,
              mb: { xs: 1, sm: 1 },
              fontWeight: 300,
              fontSize: { xs: "1.1rem", sm: "1.125rem", md: "1.25rem" },
              textAlign: { xs: "center", sm: "left" },
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" },
              gap: { xs: 0.5, sm: 1 },
              flexWrap: "wrap",
            }}
          >
            Email Conversations
            <Tooltip title="Help uploading a file?">
              <InfoOutlinedIcon
                sx={{
                  fontSize: { xs: 16, sm: 18 },
                  color: "#3898fc",
                  cursor: "pointer",
                }}
                onClick={() => setOpenHelp(true)}
              />
            </Tooltip>
          </Typography>

          <Box
            sx={{
              border: "2px dashed #99c5fa",
              backgroundColor: "#e9f3ff",
              borderRadius: 2,
              p: { xs: 2, sm: 3, md: 4 },
              textAlign: "center",
              position: "relative",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: { xs: 2, sm: 2.5 },
                justifyContent: "center",
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              <UploadIcon
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                  color: "#5c9cfa",
                  mr: { xs: 0.5, sm: 1 },
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
              >
                Supported formats for upload
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.8rem", sm: "0.825rem" },
                mb: { xs: 2, sm: 2 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Drag and drop your .eml, .msg, .mbox files
            </Typography>

            {/* Choose Files Button */}
            <Button
              variant="contained"
              startIcon={
                <UploadIcon
                  sx={{
                    fontSize: { xs: 22, sm: 24, md: 28 },
                    mb: { xs: 0.5, sm: 1 },
                    color: "white",
                  }}
                />
              }
              sx={{
                backgroundColor: "#3898fc",
                fontWeight: 500,
                height: { xs: 38, sm: 40 },
                px: { xs: 2.5, sm: 3 },
                py: 1,
                borderRadius: 1,
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 140 },
              }}
              component="label"
            >
              Choose Files
              <input
                type="file"
                hidden
                multiple
                accept=".eml,.msg,.mbox"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
        </Box>

        {/* Uploaded Files Section (Box 2) */}
        {uploadedFiles.length > 0 && (
          <Box
            sx={{
              mt: { xs: 3, sm: 4 },
              backgroundColor: "#fff",
              borderRadius: 2,
              boxShadow: "0 0 0 1px #f0f0f0",
              p: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mt: -2,
                mb: { xs: 1.5, sm: 2 },
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              Uploaded Files
            </Typography>
            <List sx={{ p: 0 }}>
              {uploadedFiles.map((file, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(index)}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.06)",
                          },
                        }}
                      >
                        <Close sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                      <Description
                        sx={{
                          color: "#1495f3",
                          fontSize: { xs: 22, sm: 26 },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: 14, sm: 16 },
                            wordBreak: "break-all",
                          }}
                        >
                          {file.name}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        >
                          {formatFileSize(file.size)} • 1 min ago
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Drawer Help Popup */}
        <Drawer
          anchor="right"
          open={openHelp}
          onClose={() => setOpenHelp(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "100vw", sm: 400, md: 500 },
              maxWidth: "100vw",
              display: "flex",
              flexDirection: "column",
              p: { xs: 2, sm: 3 },
              height: "100%",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: { xs: 1.5, sm: 2 },
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            <IconButton
              onClick={() => setOpenHelp(false)}
              edge="start"
              aria-label="back"
              sx={{ color: "black", p: { xs: 0.5, sm: 1 } }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                flex: 1,
              }}
            >
              Help uploading a file?
            </Typography>
            <IconButton
              onClick={() => setOpenHelp(false)}
              sx={{ p: { xs: 0.5, sm: 1 } }}
            >
              <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
          </Box>

          {/* Steps */}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            mb={2}
            sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}
          >
            Next Steps
          </Typography>

          <Typography
            variant="body2"
            gutterBottom
            style={{ color: "#8F8F8F" }}
            sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
          >
            <b>1. Choose Files</b>
            <br />
            Accepted formats: .eml, .msg, .mbox
          </Typography>
          <img
            src={EmailConversationHelp3}
            alt="Step"
            width="100%"
            style={{
              borderRadius: 6,
              marginBottom: 16,
              maxWidth: "100%",
              height: "auto",
            }}
          />

          <Typography
            variant="body2"
            gutterBottom
            style={{ color: "#8F8F8F" }}
            sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
          >
            <b>2. Uploaded Files</b>
            <br />
            Find your uploaded file history in this dialogue box
          </Typography>
          <img
            src={EmailConversationHelp2}
            alt="Step 2"
            width="100%"
            style={{
              borderRadius: 6,
              marginBottom: 16,
              maxWidth: "100%",
              height: "auto",
            }}
          />

          <Typography
            variant="body2"
            gutterBottom
            style={{ color: "#8F8F8F" }}
            sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
          >
            <b>3. Next</b>
            <br />
            By clicking on the "NEXT" button, the analysis of the files will be
            initiated
          </Typography>
          <img
            src={EmailConversationHelp1}
            alt="Step 3"
            width="100%"
            style={{
              borderRadius: 6,
              marginBottom: 16,
              maxWidth: "100%",
              height: "auto",
            }}
          />

          <Typography
            variant="body2"
            gutterBottom
            style={{ color: "#8F8F8F" }}
            sx={{ fontSize: { xs: "0.875rem", sm: "0.9rem" } }}
          >
            <b>4. Questionnaire</b>
            <br />
            Questionnaire will be automatically filled out based on your inputs
          </Typography>
          <img
            src={EmailConversationHelp3}
            alt="Step 4"
            width="100%"
            style={{
              borderRadius: 6,
              marginBottom: 16,
              maxWidth: "100%",
              height: "auto",
            }}
          />

          {/* Footer Buttons */}
          <Box
            sx={{
              justifyContent: "flex-end",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 },
              p: { xs: 2, sm: 4 },
              mt: "auto",
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenHelp(false)}
              sx={{
                borderColor: "#3898fc",
                color: "#3898fc",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 2,
                px: 3,
                height: { xs: 36, sm: 40 },
                fontSize: { xs: "0.875rem", sm: "1rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              onClick={() => setOpenHelp(false)}
              sx={{
                backgroundColor: "#3898fc",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 2,
                px: 3,
                height: { xs: 36, sm: 40 },
                fontSize: { xs: "0.875rem", sm: "1rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              OK
            </Button>
          </Box>
        </Drawer>
        {isAnalyzing && <LinearProgressBar label={"Analyzing..."} />}
        {isErrorPopupOpen && (
          <MessagePopup
            modalOpen={isErrorPopupOpen}
            okayHandler={() => setIsErrorPopupOpen(false)}
            message={errorMessage}
          />
        )}

        {sessionExpired && <SessionExpired />}
      </Box>

      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: 1000, xl: 1200 },
          maxWidth: "1400px",
          mx: "auto",
          mt: 3,
          mb: 3,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-end",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            onClick={() => setOpen(true)}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              borderColor: "#0094FB",
              borderWidth: 1.5,
              color: "#0094FB",
              fontSize: { xs: "0.825rem", sm: "0.9rem" },
              fontWeight: 600,
              height: { xs: 34, sm: 36 },
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 160 },
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0,148,251,0.04)",
              },
            }}
          >
            Add another source
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              backgroundColor: "#0094FB",
              textTransform: "none",
              width: { xs: "100%", sm: 100 },
              height: { xs: 34, sm: 36 },
              fontSize: { xs: "0.825rem", sm: "1rem" },
            }}
            onClick={handleAnalyzeFiles}
            endIcon={
              <ArrowForwardIcon
                sx={{
                  fontSize: { xs: 18, sm: 20 },
                }}
              />
            }
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Popup Drawer */}
      <AddSourcePopup
        open={open}
        onClose={() => setOpen(false)}
        onFileUpload={(files) =>
          setUploadedFiles((prev) => [...prev, ...files])
        }
      />
    </>
  );
};

export default EmailCommunications;
